<template>
  <div>
    <!-- Tabs Navigation -->
    <div
      class="flex border-b border-gray-200 mb-6"
    >
      <button
        v-for="tab in tabs"
        :key="tab.id"
        @click="currentTab = tab.id"
        :class="[
          'px-4 py-2 text-sm font-medium',
          currentTab === tab.id
            ? 'border-b-2 border-[#12BA6C] text-[#12BA6C]'
            : 'text-gray-500 hover:text-gray-700',
        ]"
      >
        {{ tab.name }}
      </button>
    </div>
    <div class="header-section">
      <div class="content-section">
        <!-- Users List Tab -->
        <div
          v-if="currentTab === 'users'"
          class="content mb-5"
        >
          <!-- Search and Filters -->
          <div
            class="mb-4 flex items-center justify-between"
          >
            <!-- Filter Button -->
            <div
              class="relative filter-dropdown-container"
            >
              <button
                @click.stop="
                  toggleFilterDropdown($event)
                "
                class="flex items-center gap-2 px-6 py-3 bg-[#F2F2F2] rounded-full hover:bg-gray-200"
              >
                <i
                  class="icon-smsir-filter text-[20px]"
                ></i>
                <span
                  class="text-[#0D0D0D] text-base"
                  >{{ $t("filter") }}</span
                >
              </button>

              <!-- Filter Dropdown -->
              <div
                v-if="showFilterDropdown"
                class="absolute top-full right-0 mt-1 bg-white shadow-lg rounded-3xl p-6 z-10 min-w-[384px]"
                @click.stop
              >
                <!-- Status Filter -->
                <div class="mb-4">
                  <label
                    class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t("showedByStatus") }}</label
                  >
                  <div class="space-y-2">
                    <label
                      v-for="option in statusOptions"
                      :key="option.value"
                      class="flex items-center gap-2"
                    >
                      <input
                        type="checkbox"
                        :value="option.value"
                        :checked="
                          selectedFilters.includes(
                            option.value
                          )
                        "
                        @change="
                          toggleFilter(
                            option.value
                          )
                        "
                        class="mr-2"
                      />
                      <span>
                        {{
                          option.label === "all"
                            ? $t("common.all")
                            : $t(
                                `${option.label}`
                              )
                        }}
                      </span>
                    </label>
                  </div>
                </div>
                <hr class="color-[#E6E6E6] h-[1px] w-full mb-4">
                <!-- Items Per Page -->
                <div class="mb-5">
                  <label
                    class="block text-sm font-medium text-gray-700 mb-2"
                    >{{
                      $t("itemsPerPage")
                    }}</label
                  >
                  <div class="space-y-2">
                    <label
                      v-for="option in [
                        20, 50, 100,
                      ]"
                      :key="option"
                      class="flex items-center gap-2"
                    >
                      <input
                        type="radio"
                        :value="option"
                        v-model="filters.per_page"
                        class="mr-2"
                      />
                      <span>{{ option }}</span>
                    </label>
                  </div>
                </div>

                <!-- Filter Buttons -->
                <div
                  class="flex justify-between gap-20"
                >
                  <button
                    @click="applyFilters"
                    class="py-3 px-6 bg-[#FFBF00] text-[#0D0D0D] text-base flex-1 rounded-full"
                  >
                    {{ $t("applyFilters") }}
                  </button>
                  <button
                    @click="resetFilters"
                    class="py-2 px-4 bg-white border rounded-full border-[#BE1A0E] text-[#BE1A0E] text-base flex-1"
                  >
                    {{ $t("resetFilters") }}
                  </button>
                </div>
              </div>
            </div>
            <!-- Search Input -->
            <div
              class="relative"
              style="width: 340px"
            >
              <input
                v-model="searchQuery"
                type="text"
                class="input-main w-full pl-10"
                :placeholder="
                  $t('search')
                "
                @input="debouncedSearch"
              />
              <i
                class="icon-smsir-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              ></i>
            </div>
          </div>

          <!-- Loading Spinner -->
          <LoadingSpinner v-if="loading" />

          <!-- No users message -->
          <div
            v-else-if="users.length === 0"
            class="no-templates p-4 text-center bg-gray-50 rounded-lg"
          >
            <p class="text-gray-500">
              {{ $t("noUsersFound") }}
            </p>
          </div>

          <!-- Users Table -->
          <div v-else class="overflow-x-auto">
            <table class="w-full border-collapse">
              <thead>
                <tr class="bg-gray-100">
                  <th
                    class="p-3 text-right text-[#4D4D4D] text-sm font-light"
                  >
                    {{ $t("UserName") }}
                  </th>
                  <th
                    class="p-3 text-right text-[#4D4D4D] text-sm font-light"
                  >
                    {{ $t("orderID") }}
                  </th>
                  <th
                    class="p-3 text-right text-[#4D4D4D] text-sm font-light"
                  >
                    {{ $t("status") }}
                  </th>
                  <th
                    class="p-3 text-right text-[#4D4D4D] text-sm font-light"
                  >
                    {{ $t("addedTime") }}
                  </th>
                  <th
                    class="p-3 text-right text-[#4D4D4D] text-sm font-light"
                  >
                    {{ $t("phoneNumber") }}
                  </th>
                  <th
                    class="p-3 text-right text-[#4D4D4D] text-sm font-light"
                  >
                    {{ $t("actions") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(user, index) in users"
                  :key="index"
                  class="border-b hover:bg-gray-50"
                >
                  <td
                    class="p-3 text-base text-[#0D0D0D]"
                  >
                    {{ user.name }}
                  </td>
                  <td
                    class="p-3 text-base text-[#0D0D0D]"
                  >
                    {{ user.order_id || "-" }}
                  </td>

                  <td class="p-3">
                    <span
                      :class="[
                        'px-3 py-2 rounded-full text-xs',
                        user.sms_status === 'sent'
                          ? 'bg-[#E5FFF0] text-[#0E8B51] border border-[#0E8B51]'
                          : user.sms_status ===
                            'pending'
                          ? 'bg-[#FFF9E5] text-[#CC9900] border border-[#CC9900]'
                          : 'bg-[#FDE9E7] text-[#D51D10] border border-[#D51D10]',
                      ]"
                    >
                      {{
                        getSmsStatusText(
                          user.sms_status
                        )
                      }}
                    </span>
                  </td>
                  <td
                    class="p-3 text-base text-[#0D0D0D]"
                  >
                    {{
                      formatDate(
                        user.last_activity
                      )
                    }}
                  </td>
                  <td
                    class="p-3 text-base text-[#0D0D0D]"
                  >
                    {{ user.phone }}
                  </td>
                  <td class="p-3">
                    <button
                      @click="
                        viewUserDetails(user)
                      "
                      class="text-[#0D0D0D] hover:text-blue-600"
                    >
                      <i
                        class="icon-smsir-out text-2xl"
                      ></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div
            class="pagination mt-4"
            v-if="totalPages > 1"
          >
            <button
              @click="goToPage(currentPage - 1)"
              :disabled="currentPage === 1"
              class="px-3 py-1 border rounded"
              :class="{
                'opacity-50 cursor-not-allowed':
                  currentPage === 1,
              }"
            >
              {{ $t("previous") }}
            </button>
            <span class="px-3 py-1">
              {{ currentPage }} / {{ totalPages }}
            </span>
            <button
              @click="goToPage(currentPage + 1)"
              :disabled="
                currentPage >= totalPages
              "
              class="px-3 py-1 border rounded"
              :class="{
                'opacity-50 cursor-not-allowed':
                  currentPage >= totalPages,
              }"
            >
              {{ $t("next") }}
            </button>
          </div>
        </div>

        <!-- Settings Tab -->
        <div
          v-if="currentTab === 'settings'"
          class="content mb-5"
        >
            <h3 class="section-title">
              {{ $t("enable") }}
            </h3>
            <!-- Enable/Disable Feature -->

            <div class="content mb-5 flex gap-4">
              <label class="switch peer">
                <input
                  v-model="
                    settings.leave_cart_sms
                  "
                  type="checkbox"
                />
                <div class="slider round"></div>
              </label>
              <div class="flex flex-col">
                <div class="text font-extralight">
                  {{ $t("enableLeaveCartSMS") }}
                </div>
                <div
                  class="sub-text text-xs font-extralight text-[#696767]"
                >
                  {{
                    $t(
                      "enableLeaveCartSMSDescription"
                    )
                  }}
                </div>
              </div>
            </div>

            <h3 class="section-title">
              {{ $t("smsSettings") }}
            </h3>

            <div class="flex items-center gap-4">
              <!-- Time Settings -->
              <div class="mb-6 w-1/2">
                <label
                  class="block text-sm font-normal text-[#4D4D4D] mb-1"
                >
                  {{ $t("sendSMSAfter") }} ({{
                    $t("hours")
                  }})
                </label>
                <div class="form-group">
                  <div class="input-group">
                    <input
                      type="number"
                      v-model="
                        settings.time_after
                      "
                      class="input-main"
                      min="1"
                      step="1"
                      @input="validateHours"
                    />
                  </div>
                </div>
              </div>
              <!-- Template ID -->
              <div class="mb-6 w-1/2">
                <label
                  class="block text-sm font-normal text-[#4D4D4D] mb-1"
                >
                  {{ $t("templateId") }}
                </label>
                <input
                  v-model="settings.template_id"
                  type="number"
                  class="input-main w-full"
                  :placeholder="
                    $t('enterTemplateId')
                  "
                />
              </div>
            </div>

            <div class="flex gap-4">
              <!-- Discount Code -->
              <div class="mb-6 w-1/2">
                <label
                  class="block text-sm font-normal text-[#4D4D4D] mb-1"
                >
                  {{ $t("discountCode") }}
                </label>
                <input
                  v-model="settings.discount_code"
                  type="text"
                  class="input-main w-full"
                  :placeholder="
                    $t('enterDiscountCode')
                  "
                />
              </div>

              <div class="mb-6 w-1/2">
                <label
                  class="block text-sm font-normal text-[#4D4D4D] mb-1"
                >
                  {{ $t("messageTemplate") }}
                </label>
                <div class="relative">
                  <textarea
                    v-model="
                      settings.message_template
                    "
                    class="input-main-textarea w-full h-32"
                    :placeholder="
                      $t('enterMessageText')
                    "
                  ></textarea>

                  <div class="mt-2">
                    <div
                      class="text-sm text-gray-700 mb-1 w-full"
                    >
                      {{
                        $t("availableParameters")
                      }}:
                    </div>
                    <div
                      class="flex flex-wrap gap-2"
                    >
                      <button
                        v-for="param in availableParameters"
                        :key="param.id"
                        @click="
                          insertParameter(
                            param.placeholder
                          )
                        "
                        class="text-xs bg-gray-200 hover:bg-gray-300 rounded px-2 py-1"
                      >
                        {{ param.label }} ({{
                          param.placeholder
                        }})
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Save Button -->
            <div class="flex justify-end">
              <button
                @click="saveSettings"
                class="BTNMAIN bg-[#12BA6C] flex gap-2 items-center"
                :disabled="saving"
              >
                <i
                  class="icon-smsir-save text-xl text-white"
                ></i>
                <span
                  class="text-white text-base"
                >
                  {{ $t("saveSettings") }}
                </span>
              </button>
            </div>
        
        </div>
      </div>
    </div>

    <!-- User Details Modal -->
    <div
      v-if="showUserDetailsModal"
      class="modal"
    >
      <div class="modal-content max-w-2xl">
        <div class="modal-header">
          <h2 class="text-xl font-bold">
            {{ $t("common.details") }}
          </h2>
          <button
            @click="closeUserDetailsModal"
            class="text-2xl"
          >
            &times;
          </button>
        </div>

        <div class="modal-body">
          <div
            v-if="selectedUser"
            class="space-y-4"
          >
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label
                  class="block text-sm font-medium text-gray-700"
                  >{{ $t("UserName") }}</label
                >
                <p class="mt-1">
                  {{ selectedUser.name }}
                </p>
              </div>
              <div>
                <label
                  class="block text-sm font-medium text-gray-700"
                  >{{ $t("phoneNumber") }}</label
                >
                <p class="mt-1">
                  {{ selectedUser.phone }}
                </p>
              </div>
              <div>
                <label
                  class="block text-sm font-medium text-gray-700"
                  >{{ $t("orderID") }}</label
                >
                <p class="mt-1">
                  {{
                    selectedUser.order_id || "-"
                  }}
                </p>
              </div>
              <div>
                <label
                  class="block text-sm font-medium text-gray-700"
                  >{{ $t("addedTime") }}</label
                >
                <p class="mt-1">
                  {{
                    formatDate(
                      selectedUser.last_activity
                    )
                  }}
                </p>
              </div>

              <div>
                <label
                  class="block text-sm font-medium text-gray-700"
                  >{{ $t("discountCode") }}</label
                >
                <p class="mt-1">
                  {{
                    selectedUser.discount_code ||
                    "-"
                  }}
                </p>
              </div>
            </div>

            <div>
              <label
                class="block text-sm font-medium text-gray-700"
                >{{ $t("messageSent") }}</label
              >
              <p
                class="mt-1 bg-gray-50 p-3 rounded"
              >
                {{ selectedUser.message_sent }}
              </p>
            </div>

            <!-- SMS Section -->
            <div class="mt-6 border-t pt-6">
              <h3
                class="text-lg font-medium mb-4"
              >
                {{ $t("sendSMS") }}
              </h3>

              <div class="space-y-4">
                <!-- SMS Text -->
                <div>
                  <label
                    class="block text-sm font-medium text-gray-700 mb-1"
                    >{{ $t("smsText") }}</label
                  >
                  <textarea
                    v-model="smsText"
                    class="input-main-textarea w-full h-32"
                    :placeholder="
                      $t('enterMessageText')
                    "
                  ></textarea>
                </div>

                <!-- Send Button -->
                <div class="flex justify-end">
                  <button
                    @click="sendSms"
                    class="BTNMAIN bg-[#12BA6C] flex gap-2 items-center"
                    :disabled="sendingSms"
                  >
                    <span
                      v-if="sendingSms"
                      class="flex items-center gap-1"
                    >
                      <i
                        class="icon-smsir-send text-xl animate-spin text-white"
                      ></i>
                      <span
                        class="text-white text-base"
                      >
                        {{ $t("sending") }}
                      </span>
                    </span>
                    <span
                      v-else
                      class="flex items-center gap-1"
                    >
                      <i
                        class="icon-smsir-send text-xl text-white"
                      ></i>
                      <span
                        class="text-white text-base"
                      >
                        {{ $t("sendSMS") }}
                      </span>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            @click="closeUserDetailsModal"
            class="px-4 py-2 border border-gray-300 rounded-full hover:bg-gray-100 text-base"
          >
            {{ $t("common.close") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import LoadingSpinner from "../LoadingSpinner.vue";

export default {
  name: "LeaveCartSettings",
  components: {
    LoadingSpinner,
  },
  data() {
    return {
      currentTab: "users",
      tabs: [
        {
          id: "users",
          name: this.$t("usersList"),
        },
        {
          id: "settings",
          name: this.$t("settings"),
        },
      ],
      loading: true,
      saving: false,
      users: [],
      selectedUser: null,
      showUserDetailsModal: false,
      smsTemplateId: "",
      smsText: "",
      sendingSms: false,
      settings: {
        leave_cart_sms: false,
        time_after: 1,
        template_id: "",
        message_template: "",
        discount_code: "",
      },
      availableParameters: [
        {
          id: 1,
          label: this.$t("UserName"),
          placeholder: "#UserName#",
        },
        {
          id: 2,
          label: this.$t("discountCode"),
          placeholder: "#DiscountCode#",
        },
        {
          id: 3,
          label: this.$t("cartItems"),
          placeholder: "#CartItems#",
        },
        {
          id: 4,
          label: this.$t("cartTotal"),
          placeholder: "#CartTotal#",
        },
      ],
      timeError: "",
      pollInterval: null,
      searchQuery: "",
      showFilterDropdown: false,
      selectedFilters: ["all"],
      filters: {
        status: "all",
        per_page: 20,
      },
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      debounceTimer: null,
    };
  },
  computed: {
    statusOptions() {
      return [
        { value: "all", label: "all" },
        { value: "pending", label: "pendingToSend" },
        { value: "sent", label: "sent" },
      ];
    },
  },
  mounted() {
    this.loadSettings();
    this.loadUsers();
    this.startPolling();
    document.addEventListener(
      "click",
      this.handleClickOutside
    );
  },
  beforeDestroy() {
    this.stopPolling();
    document.removeEventListener(
      "click",
      this.handleClickOutside
    );
  },
  methods: {
    startPolling() {
      this.stopPolling();
      this.pollInterval = setInterval(() => {
        this.loadUsers();
      }, 5 * 60 * 1000);
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },

    async loadUsers() {
      this.loading = true;
      try {
        const params = new URLSearchParams({
          action: "sms_ir_get_leave_cart_users",
          nonce: window.sms_ir_login.nonce,
          page: this.currentPage,
          per_page: this.filters.per_page,
          status: this.filters.status,
          search: this.searchQuery,
        });

        const response = await axios.post(
          window.sms_ir_login.ajaxUrl,
          params,
          {
            headers: {
              "Content-Type":
                "application/x-www-form-urlencoded",
            },
          }
        );

        if (response.data.success) {
          this.users = response.data.data.items;
          this.totalItems =
            response.data.data.total_items;
          this.totalPages =
            response.data.data.total_pages;
        } else {
          throw new Error(
            response.data.data ||
              this.$t("FailedToLoadUsers")
          );
        }
      } catch (error) {
        console.error(
          "Error loading users:",
          error
        );
        Swal.fire({
          text: this.$t("FailedToLoadUsers"),
          icon: "error",
          toast: true,
          position: "top-start",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: "smsir-swal",
          },
        });
      } finally {
        this.loading = false;
      }
    },

    async loadSettings() {
      try {
        const response = await axios.post(
          window.sms_ir_login.ajaxUrl,
          new URLSearchParams({
            action:
              "sms_ir_get_leave_cart_settings",
            nonce: window.sms_ir_login.nonce,
          }),
          {
            headers: {
              "Content-Type":
                "application/x-www-form-urlencoded",
            },
          }
        );

        if (response.data.success) {
          this.settings = response.data.data;
        }
      } catch (error) {
        console.error(
          "Error loading settings:",
          error
        );
      }
    },

    validateTimeInput() {
      if (this.settings.time_after < 1) {
        this.timeError = this.$t(
          "timeMustBeGreaterThanZero"
        );
        this.settings.time_after = 1;
      } else if (this.settings.time_after > 999) {
        this.timeError = this.$t(
          "timeMustBeLessThan1000"
        );
        this.settings.time_after = 999;
      } else {
        this.timeError = "";
      }
    },

    async saveSettings() {
      if (this.timeError) {
        Swal.fire({
          text: this.$t("PleaseFixTimeError"),
          icon: "error",
          toast: true,
          position: "top-start",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: "smsir-swal",
          },
        });
        return;
      }

      this.saving = true;
      try {
        const response = await axios.post(
          window.sms_ir_login.ajaxUrl,
          new URLSearchParams({
            action:
              "sms_ir_save_leave_cart_settings",
            nonce: window.sms_ir_login.nonce,
            settings: JSON.stringify(
              this.settings
            ),
          }),
          {
            headers: {
              "Content-Type":
                "application/x-www-form-urlencoded",
            },
          }
        );

        if (response.data.success) {
          Swal.fire({
            text: this.$t(
              "SettingsSavedSuccessfully"
            ),
            icon: "success",
            toast: true,
            position: "top-start",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: {
              popup: "smsir-swal",
            },
          });
        } else {
          throw new Error(
            response.data.data ||
              this.$t("SettingsSaveError")
          );
        }
      } catch (error) {
        console.error(
          "Error saving settings:",
          error
        );
        Swal.fire({
          text: this.$t("SettingsSaveError"),
          icon: "error",
          toast: true,
          position: "top-start",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: "smsir-swal",
          },
        });
      } finally {
        this.saving = false;
      }
    },

    viewUserDetails(user) {
      this.selectedUser = user;
      this.showUserDetailsModal = true;
    },

    closeUserDetailsModal() {
      this.selectedUser = null;
      this.showUserDetailsModal = false;
    },

    openSmsModal(user) {
      this.selectedUser = user;
      this.smsText = "";
    },

    closeSmsModal() {
      this.selectedUser = null;
      this.smsText = "";
    },

    async sendSms() {
      if (!this.smsText) {
        Swal.fire({
          text: this.$t(
            "PleaseEnterText"
          ),
          icon: "error",
          toast: true,
          position: "top-start",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: "smsir-swal",
          },
        });
        return;
      }

      this.sendingSms = true;
      try {
        const response = await axios.post(
          window.sms_ir_login.ajaxUrl,
          new URLSearchParams({
            action: "sms_ir_send_leave_cart_sms",
            nonce: window.sms_ir_login.nonce,
            user_id: this.selectedUser.id,
            message: this.smsText,
          }),
          {
            headers: {
              "Content-Type":
                "application/x-www-form-urlencoded",
            },
          }
        );

        if (response.data.success) {
          Swal.fire({
            text: this.$t("SmsSentSuccessfully"),
            icon: "success",
            toast: true,
            position: "top-start",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: {
              popup: "smsir-swal",
            },
          });
          this.closeSmsModal();
          this.loadUsers(); // Refresh the list
        } else {
          throw new Error(
            response.data.data ||
              this.$t("FailedToSendSms")
          );
        }
      } catch (error) {
        console.log(error.response);
        const errorMessage =
          error.response?.data?.data ||
          this.$t("FailedToSendSms");
        Swal.fire({
          text: errorMessage,
          icon: "error",
          toast: true,
          position: "top-start",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: "smsir-swal",
          },
        });
      } finally {
        this.sendingSms = false;
      }
    },

    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleString();
    },

    getSmsStatusText(status) {
      switch (status) {
        case "sent":
          return this.$t("sent");
        case "pending":
          return this.$t("pendingToSend");
        case "failed":
          return this.$t("failed");
        default:
          return status;
      }
    },

    insertParameter(parameter) {
      const textarea =
        this.$el.querySelector("textarea");
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = this.settings.message_template;

      this.settings.message_template =
        text.substring(0, start) +
        parameter +
        text.substring(end);

      this.$nextTick(() => {
        textarea.focus();
        textarea.setSelectionRange(
          start + parameter.length,
          start + parameter.length
        );
      });
    },

    validateHours() {
      // Ensure the value is a positive integer
      if (this.settings.time_after < 1) {
        this.settings.time_after = 1;
      }
      this.settings.time_after = Math.floor(
        this.settings.time_after
      );
    },

    toggleFilterDropdown(event) {
      event.stopPropagation();
      this.showFilterDropdown =
        !this.showFilterDropdown;
    },

    debouncedSearch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.currentPage = 1;
        this.loadUsers();
      }, 500);
    },

    toggleFilter(value) {
      if (value === "all") {
        // If 'all' is selected, clear other filters
        this.selectedFilters = ["all"];
      } else {
        // Remove 'all' from filters
        const index =
          this.selectedFilters.indexOf("all");
        if (index !== -1) {
          this.selectedFilters =
            this.selectedFilters.filter(
              (f) => f !== "all"
            );
        }

        // Toggle the selected filter
        const valueIndex =
          this.selectedFilters.indexOf(value);
        if (valueIndex === -1) {
          // Add the filter
          this.selectedFilters.push(value);
        } else {
          // Remove the filter
          this.selectedFilters.splice(
            valueIndex,
            1
          );
        }

        // If no filters are selected, select 'all'
        if (this.selectedFilters.length === 0) {
          this.selectedFilters = ["all"];
        }
      }
    },

    applyFilters() {
      this.currentPage = 1;
      this.filters.status =
        this.selectedFilters.join(",");
      this.loadUsers();
      this.showFilterDropdown = false;
    },

    resetFilters() {
      this.selectedFilters = ["all"];
      this.filters = {
        status: "all",
        per_page: 20,
      };
      this.searchQuery = "";
      this.currentPage = 1;
      this.loadUsers();
      this.showFilterDropdown = false;
    },

    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.loadUsers();
      }
    },

    handleClickOutside(event) {
      const dropdown = this.$el.querySelector(
        ".filter-dropdown-container"
      );
      if (
        dropdown &&
        !dropdown.contains(event.target) &&
        this.showFilterDropdown
      ) {
        this.showFilterDropdown = false;
      }
    },
  },
};
</script>

<style scoped>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 24px;
  border-radius: 30px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-shrink: 0;
}

.modal-header h2 {
  font-size: 18px;
  font-weight: 600;
  color: #0d0d0d;
}

.modal-body {
  overflow-y: auto;
  flex: 1;
  padding: 0 16px;
}

.modal-footer {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
  padding-top: 16px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
}

.filter-dropdown-container {
  position: relative;
}

/* Status badge styles */
.status-badge {
  display: inline-block;
  padding: 8.5px 12px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 400;
}

.status-badge.pending {
  background-color: #fff9e5;
  border: 1px solid #b38600;
  color: #b38600;
}

.status-badge.sent {
  background-color: #e5fff0;
  border: 1px solid #0e8b51;
  color: #0e8b51;
}

.status-badge.all {
  background-color: #f2f2f2;
  border: 1px solid #4d4d4d;
  color: #4d4d4d;
}

/* Add this to close the dropdown when clicking outside */
@media (min-width: 768px) {
  .modal-content {
    width: 600px;
  }
}
</style>
